import { ChangeDetectionStrategy, Component, computed, effect, inject, model, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoService } from '@ngneat/transloco';
import { SharedModule } from '@shared/shared.module';
import { HdContentLanguageService } from '../hd-content-language.service';

/** Doel van de moment-bewaar-dialog (tab-contextmenu); null = gesloten. */
export interface HdMomentSaveTarget {
  /** Voorgestelde naam: "{chartnaam} {DD/MM/YY HH:mm}" (beslispunt 5). */
  defaultName: string;
  /** Weergave van het bewaarde moment (lokaal geformatteerd) in de dialoogtekst. */
  momentText: string;
  /** Foutmelding van een vorige poging (bv. naamclash) — dialog blijft dan open. */
  errorText?: string;
}

/** Klein dialoogje om de just-now overlay als moment-chart te bewaren. */
@Component({
  selector: 'hd-moment-save-dialog',
  standalone: true,
  imports: [SharedModule, FormsModule],
  templateUrl: './hd-moment-save-dialog.component.html',
  styleUrl: './hd-moment-save-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdMomentSaveDialogComponent {
  readonly target = model<HdMomentSaveTarget | null>(null);
  readonly confirmed = output<string>();

  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);

  protected readonly name = signal('');

  constructor() {
    effect(() => {
      const target = this.target();
      if (target != null) {
        this.name.set(target.defaultName);
      }
    });
  }

  protected readonly text = computed(() => {
    const lang = this.contentLang.effectiveLang();
    const translate = (key: string) => this.transloco.translate<string>(key, undefined, lang);
    return {
      title: translate('humanDesign.chart.momentSaveDialog.title'),
      intro: translate('humanDesign.chart.momentSaveDialog.intro'),
      placeholder: translate('humanDesign.chart.momentSaveDialog.namePlaceholder'),
      save: translate('humanDesign.chart.momentSaveDialog.save'),
    };
  });

  protected get canSave(): boolean {
    return this.name().trim().length > 0;
  }

  protected onOpenChange(open: boolean): void {
    if (!open) {
      this.target.set(null);
    }
  }

  protected submit(): void {
    const name = this.name().trim();
    if (!name) {
      return;
    }
    this.confirmed.emit(name);
    // De page sluit de dialog bij succes (target op null) of heropent hem met errorText.
  }
}
